/**
 * Formatting module for chart data (axes and list columns).
 *
 * The sales server describes how each value should be rendered with small JSON
 * definitions. This module turns those definitions into formatter functions.
 *
 * Number format (used for the X and Y axis, and for `number` list columns):
 *   {
 *     "type":     "short" | "long" | "full" | "percent", // formatting before the comma
 *     "fraction": number,                                 // number of decimal places
 *     "currency": "EUR" | "USD" | "GBP" | ...             // ISO code -> currency symbol
 *   }
 *   - short:   1234     (no grouping)
 *   - long:    1,234    (grouping)
 *   - full:    +1,234   (grouping + explicit sign)
 *   - percent: 12 %     (the value is multiplied by 100)
 *
 * Date format (used for `date` list columns). Each date component is rendered
 * independently. A missing component is omitted from the output:
 *   day     short: 2     long: 02     full: Tue, 2.
 *   month   short: 06    long: Aug    full: August
 *   year    short: 26    long/full: 2026
 *   hour    short: 8     long/full: 08
 *   minute  short/long/full: 35
 *   second  short/long/full: 15
 *   week    short: 23    long/full: week 23
 *   quarter short: 2     long/full: Q2
 *
 * NOTE: the server emits the number format key as `style` in real responses
 * while the spec documents it as `type`; both are accepted here.
 */

export type NumberFormatType = 'short' | 'long' | 'full' | 'percent'

export interface NumberFormat {
  /** Format before the comma. Documented as `type`, emitted as `style`. */
  type?: NumberFormatType
  style?: NumberFormatType
  /** Number of decimal places. */
  fraction?: number
  /** ISO currency code, e.g. "EUR", "USD", "GBP". */
  currency?: string
}

export type DateComponentStyle = 'short' | 'long' | 'full'

export interface DateFormat {
  day?: DateComponentStyle
  month?: DateComponentStyle
  year?: DateComponentStyle
  hour?: DateComponentStyle
  minute?: DateComponentStyle
  second?: DateComponentStyle
  week?: DateComponentStyle
  quarter?: DateComponentStyle
}

export type ColumnType = 'string' | 'number' | 'date'

/** A single entry of `columnStyles`. The date/number format lives under `format`. */
export interface ColumnStyle {
  format?: DateFormat & NumberFormat
}

const DEFAULT_LOCALE = 'en'

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

function resolveNumberType(format: NumberFormat): NumberFormatType {
  return format.type ?? format.style ?? 'long'
}

/**
 * Builds a formatter for a number format definition. Returns a function that
 * turns a number into its localized string representation. Non-finite inputs
 * are returned as-is so the caller never crashes on bad data.
 */
export function getNumberFormatter(
  format: NumberFormat,
  locale: string = DEFAULT_LOCALE,
): (value: number) => string {
  const type = resolveNumberType(format)
  const options: Intl.NumberFormatOptions = {}

  if (typeof format.fraction === 'number') {
    options.minimumFractionDigits = format.fraction
    options.maximumFractionDigits = format.fraction
  }

  switch (type) {
    case 'short':
      options.useGrouping = false
      break
    case 'long':
      options.useGrouping = true
      break
    case 'full':
      options.useGrouping = true
      options.signDisplay = 'exceptZero'
      break
    case 'percent':
      // Intl multiplies by 100 internally, matching the spec.
      options.style = 'percent'
      options.useGrouping = true
      break
  }

  // Currency takes precedence over the plain decimal/percent style.
  if (format.currency) {
    options.style = 'currency'
    options.currency = format.currency
  }

  const formatter = new Intl.NumberFormat(locale, options)

  return (value: number) =>
    Number.isFinite(value) ? formatter.format(value) : String(value)
}

function getISOWeek(date: Date): number {
  // ISO 8601 week number, based on the Thursday of the current week.
  const utc = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  )
  const dayOfWeek = utc.getUTCDay() || 7
  utc.setUTCDate(utc.getUTCDate() + 4 - dayOfWeek)
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1))
  return Math.ceil(((utc.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7)
}

function formatDay(
  date: Date,
  style: DateComponentStyle,
  locale: string,
): string {
  const dayOfMonth = date.getDate()
  switch (style) {
    case 'short':
      return String(dayOfMonth)
    case 'long':
      return pad2(dayOfMonth)
    case 'full': {
      const weekday = new Intl.DateTimeFormat(locale, {
        weekday: 'short',
      }).format(date)
      return `${weekday}, ${dayOfMonth}.`
    }
  }
}

function formatMonth(
  date: Date,
  style: DateComponentStyle,
  locale: string,
): string {
  switch (style) {
    case 'short':
      return pad2(date.getMonth() + 1)
    case 'long':
      return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date)
    case 'full':
      return new Intl.DateTimeFormat(locale, { month: 'long' }).format(date)
  }
}

function formatYear(date: Date, style: DateComponentStyle): string {
  return style === 'short'
    ? String(date.getFullYear()).slice(-2)
    : String(date.getFullYear())
}

function formatHour(date: Date, style: DateComponentStyle): string {
  return style === 'short' ? String(date.getHours()) : pad2(date.getHours())
}

function formatWeek(date: Date, style: DateComponentStyle): string {
  const week = getISOWeek(date)
  return style === 'short' ? String(week) : `week ${week}`
}

function formatQuarter(date: Date, style: DateComponentStyle): string {
  const quarter = Math.floor(date.getMonth() / 3) + 1
  return style === 'short' ? String(quarter) : `Q${quarter}`
}

/**
 * Builds a formatter for a date format definition. The returned function
 * accepts an ISO date string, a timestamp or a Date and composes only the
 * requested components. Unparseable values are returned as a string.
 */
type DatePartType = 'day' | 'month' | 'year'

function getNumericDateLayout(locale: string): {
  separator: string
  order: DatePartType[]
} {
  const parts = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(2000, 0, 1))

  const separator = parts.find((p) => p.type === 'literal')?.value ?? '/'
  const order = parts
    .filter((p): p is Intl.DateTimeFormatPart & { type: DatePartType } =>
      ['day', 'month', 'year'].includes(p.type),
    )
    .map((p) => p.type)

  return { separator, order }
}

export function getDateFormatter(
  format: DateFormat,
  locale: string = DEFAULT_LOCALE,
): (value: string | number | Date) => string {
  // When month is 'short' all parts are numeric — use locale-aware separator
  // and order (e.g. en-US: MM/DD/YYYY, de-DE/fr-FR: DD.MM.YYYY or DD/MM/YYYY).
  const numericLayout =
    format.month === 'short' ? getNumericDateLayout(locale) : null

  return (value: string | number | Date) => {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) {
      return value == null ? '' : String(value)
    }

    let calendar: string[]
    if (numericLayout) {
      const partMap: Record<DatePartType, string | undefined> = {
        day: format.day ? formatDay(date, format.day, locale) : undefined,
        month: format.month
          ? formatMonth(date, format.month, locale)
          : undefined,
        year: format.year ? formatYear(date, format.year) : undefined,
      }
      calendar = numericLayout.order
        .map((k) => partMap[k])
        .filter((v): v is string => v !== undefined)
    } else {
      calendar = []
      if (format.quarter) calendar.push(formatQuarter(date, format.quarter))
      if (format.week) calendar.push(formatWeek(date, format.week))
      if (format.day) calendar.push(formatDay(date, format.day, locale))
      if (format.month) calendar.push(formatMonth(date, format.month, locale))
      if (format.year) calendar.push(formatYear(date, format.year))
    }

    const clock: string[] = []
    if (format.hour) clock.push(formatHour(date, format.hour))
    if (format.minute) clock.push(pad2(date.getMinutes()))
    if (format.second) clock.push(pad2(date.getSeconds()))

    const sep = numericLayout?.separator ?? ' '
    const segments: string[] = [calendar.join(sep)]
    if (clock.length > 0) segments.push(clock.join(':'))

    return segments.filter(Boolean).join(' ')
  }
}

/**
 * Returns a number formatter for an axis when a format definition is present,
 * otherwise the supplied fallback. Lets the chart converters keep their
 * existing default rendering when the server omits `xFormat`/`yFormat`.
 */
export function getAxisLabelFormatter(
  format: NumberFormat | undefined,
  locale: string,
  fallback: (value: number) => string,
): (value: number) => string {
  return format ? getNumberFormatter(format, locale) : fallback
}

/**
 * Formats a single list cell according to its column type and format
 * definition. `string` columns and missing values are passed through.
 */
export function formatColumnValue(
  value: string | number | null | undefined,
  columnType: ColumnType | undefined,
  format: (DateFormat & NumberFormat) | undefined,
  locale: string = DEFAULT_LOCALE,
): string {
  if (value == null) return ''

  if (columnType === 'date' && format) {
    return getDateFormatter(format, locale)(value)
  }

  if (columnType === 'number' && format) {
    const numeric = typeof value === 'number' ? value : parseFloat(value)
    return getNumberFormatter(format, locale)(numeric)
  }

  return String(value)
}
